import { useRef, useMemo, Component, Suspense } from "react";
import { useFrame } from "@react-three/fiber";
import { MeshReflectorMaterial, RoundedBox, Billboard, Text, useTexture } from "@react-three/drei";
import { media } from "@/lib/api";
import { COLORS } from "../world";
import * as THREE from "three";

// Null-fallback error boundary so a single failed texture can't crash the canvas
export class Safe extends Component {
  constructor(p) { super(p); this.state = { e: false }; }
  static getDerivedStateFromError() { return { e: true }; }
  render() { return this.state.e ? (this.props.fallback || null) : this.props.children; }
}

// Unlit, full-bright textured plane for product vial photos (reliable across GPUs)
export function TexPlane({ url, scale = [1.5, 1.5] }) {
  const tex = useTexture(url);
  if (tex) tex.colorSpace = THREE.SRGBColorSpace;
  return (
    <mesh>
      <planeGeometry args={scale} />
      <meshBasicMaterial map={tex} transparent toneMapped={false} side={THREE.DoubleSide} />
    </mesh>
  );
}

export function Floor({ reflect = true, color = COLORS.cyan }) {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
      <planeGeometry args={[60, 60]} />
      {reflect ? (
        <MeshReflectorMaterial
          resolution={512} mixBlur={1} mixStrength={6} blur={[400, 100]}
          roughness={0.7} depthScale={1.1} minDepthThreshold={0.4} maxDepthThreshold={1.3}
          color="#05070a" metalness={0.75} mirror={0.45}
        />
      ) : (
        <meshStandardMaterial color="#05070a" metalness={0.6} roughness={0.5} />
      )}
    </mesh>
  );
}

export function RoomShell({ accent = COLORS.cyan, reflect = true }) {
  return (
    <group>
      <Floor reflect={reflect} color={accent} />
      {/* back + side walls */}
      {[[0, -22, 0], [0, 22, Math.PI], [-22, 0, Math.PI / 2], [22, 0, -Math.PI / 2]].map(([x, z, ry], i) => (
        <mesh key={i} position={[x, 6, z]} rotation={[0, ry, 0]}>
          <planeGeometry args={[44, 12]} />
          <meshStandardMaterial color="#070b11" metalness={0.5} roughness={0.35} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {/* ceiling */}
      <mesh position={[0, 12, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[44, 44]} />
        <meshStandardMaterial color="#04060a" metalness={0.4} roughness={0.6} side={THREE.DoubleSide} />
      </mesh>
      {/* circular ceiling light rings */}
      {[5, 9, 13].map((r, i) => (
        <mesh key={i} position={[0, 11.6, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[r, r + 0.18, 64]} />
          <meshBasicMaterial color={accent} toneMapped={false} transparent opacity={0.6} />
        </mesh>
      ))}
      {/* floor accent ring */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[15.6, 15.9, 80]} />
        <meshBasicMaterial color={accent} toneMapped={false} transparent opacity={0.35} />
      </mesh>
    </group>
  );
}

export function DNAChamber() {
  const g = useRef();
  const pts = useMemo(() => {
    const a = [];
    for (let i = 0; i < 44; i++) {
      const t = i / 44 * Math.PI * 4;
      const y = i * 0.14;
      a.push([Math.cos(t) * 0.9, y, Math.sin(t) * 0.9]);
      a.push([Math.cos(t + Math.PI) * 0.9, y, Math.sin(t + Math.PI) * 0.9]);
    }
    return a;
  }, []);
  useFrame((_, d) => { if (g.current) g.current.rotation.y += d * 0.25; });
  return (
    <group position={[0, 0.2, 0]}>
      {/* glass cylinder */}
      <mesh position={[0, 3.3, 0]}>
        <cylinderGeometry args={[1.7, 1.7, 6.6, 48, 1, true]} />
        <meshPhysicalMaterial color="#9fe8ff" transparent opacity={0.08} roughness={0} metalness={0} transmission={1} thickness={0.5} side={THREE.DoubleSide} />
      </mesh>
      <group ref={g} position={[0, 0.4, 0]}>
        {pts.map((p, i) => (
          <mesh key={i} position={p}>
            <sphereGeometry args={[0.12, 12, 12]} />
            <meshBasicMaterial color={i % 2 ? COLORS.violet : COLORS.cyan} toneMapped={false} />
          </mesh>
        ))}
      </group>
      <pointLight position={[0, 3.5, 0]} color={COLORS.cyan} intensity={8} distance={12} />
    </group>
  );
}

// A premium illuminated product display: a billboarded framed card (always
// faces the player) so the full-scene vial photo is clearly visible + readable.
export function ProductDisplay({ product, position = [0, 0, 0], accent = COLORS.cyan, onInspect }) {
  const imgUrl = media(product.image);
  const price = product.from_price || product.price || 0;
  const click = (e) => { e.stopPropagation(); onInspect && onInspect(product.slug); };
  return (
    <group position={position}>
      {/* pedestal */}
      <RoundedBox args={[1.4, 0.9, 1.4]} radius={0.07} position={[0, 0.45, 0]}>
        <meshStandardMaterial color="#0b1118" metalness={0.8} roughness={0.25} />
      </RoundedBox>
      <mesh position={[0, 0.92, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.55, 0.68, 48]} />
        <meshBasicMaterial color={accent} toneMapped={false} />
      </mesh>
      {/* floating framed card */}
      <Billboard position={[0, 2.5, 0]}>
        <mesh position={[0, 0, -0.06]}><planeGeometry args={[1.98, 2.5]} /><meshBasicMaterial color={accent} transparent opacity={0.25} toneMapped={false} /></mesh>
        <mesh position={[0, 0, -0.04]}><planeGeometry args={[1.82, 2.34]} /><meshBasicMaterial color="#05080d" /></mesh>
        <group position={[0, 0.34, 0]}>
          <Safe><Suspense fallback={null}><TexPlane url={imgUrl} scale={[1.56, 1.56]} /></Suspense></Safe>
        </group>
        <Text position={[0, -0.64, 0.02]} fontSize={0.135} color="#eaf6ff" anchorX="center" anchorY="top" maxWidth={1.72} textAlign="center" lineHeight={1.1} outlineWidth={0.005} outlineColor="#02060a">{product.name}</Text>
        <Text position={[0, -1.12, 0.02]} fontSize={0.14} color={accent} anchorX="center" anchorY="middle">${price.toFixed(2)}</Text>
        {/* click target */}
        <mesh position={[0, 0, 0.1]} onClick={click}
          onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
          onPointerOut={() => { document.body.style.cursor = "default"; }}>
          <planeGeometry args={[1.98, 2.5]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      </Billboard>
    </group>
  );
}

export function HoloSign({ label, color = COLORS.cyan, position, angle = 0, onClick }) {
  const ref = useRef();
  useFrame((st) => { if (ref.current) ref.current.position.y = 2.4 + Math.sin(st.clock.elapsedTime * 1.5) * 0.05; });
  return (
    <group position={position} rotation={[0, -angle + Math.PI / 2, 0]}>
      <mesh
        ref={ref}
        position={[0, 2.4, 0]}
        onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { document.body.style.cursor = "default"; }}
      >
        <planeGeometry args={[2.4, 0.9]} />
        <meshBasicMaterial color="#060a10" transparent opacity={0.65} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 1.0, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 2.0, 8]} />
        <meshBasicMaterial color={color} toneMapped={false} transparent opacity={0.4} />
      </mesh>
      <Billboard position={[0, 2.4, 0.08]}>
        <Text fontSize={0.42} color={color} anchorX="center" anchorY="middle" outlineWidth={0.012} outlineColor="#02060a" letterSpacing={0.18}>{label}</Text>
      </Billboard>
    </group>
  );
}

export function ScientistAvatar({ sci, position, onTalk }) {
  const color = sci.accent || COLORS.violet;
  const ref = useRef();
  useFrame((st) => { if (ref.current) ref.current.rotation.y = Math.sin(st.clock.elapsedTime * 0.5) * 0.3; });
  return (
    <group position={position}>
      <group ref={ref}>
        {/* body */}
        <mesh position={[0, 1.0, 0]}><capsuleGeometry args={[0.34, 1.0, 6, 14]} /><meshStandardMaterial color="#0d1420" metalness={0.6} roughness={0.3} emissive={color} emissiveIntensity={0.08} /></mesh>
        {/* head */}
        <mesh position={[0, 1.95, 0]}><sphereGeometry args={[0.26, 24, 24]} /><meshStandardMaterial color="#111a28" metalness={0.5} roughness={0.35} emissive={color} emissiveIntensity={0.12} /></mesh>
        {/* visor */}
        <mesh position={[0, 1.97, 0.2]}><boxGeometry args={[0.34, 0.1, 0.06]} /><meshBasicMaterial color={color} toneMapped={false} /></mesh>
      </group>
      {/* halo ring */}
      <mesh position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]}><ringGeometry args={[0.8, 0.95, 40]} /><meshBasicMaterial color={color} toneMapped={false} transparent opacity={0.6} /></mesh>
      <pointLight position={[0, 1.6, 0.4]} color={color} intensity={2} distance={4} />
      <Billboard position={[0, 2.55, 0]}>
        <Text fontSize={0.3} color={color} anchorX="center" anchorY="middle" outlineWidth={0.008} outlineColor="#02060a">{sci.name}</Text>
        <Text position={[0, -0.34, 0]} fontSize={0.15} color="#cfe9ff" anchorX="center" anchorY="middle" letterSpacing={0.12}>AI RESEARCH SCIENTIST</Text>
      </Billboard>
      <mesh position={[0, 1.1, 0]} onClick={(e) => { e.stopPropagation(); onTalk && onTalk(sci.key); }}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { document.body.style.cursor = "default"; }}>
        <cylinderGeometry args={[0.6, 0.6, 2.4, 12]} /><meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </group>
  );
}

export function Console({ label = "CONSOLE", color = COLORS.mint, position = [0, 0, 0], onClick }) {
  return (
    <group position={position}>
      <RoundedBox args={[3.2, 1.1, 1.2]} radius={0.08} position={[0, 0.75, 0]}>
        <meshStandardMaterial color="#0b1118" metalness={0.8} roughness={0.25} />
      </RoundedBox>
      <mesh position={[0, 1.45, 0.1]} rotation={[-Math.PI / 3.2, 0, 0]}
        onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { document.body.style.cursor = "default"; }}>
        <planeGeometry args={[2.9, 1.5]} />
        <meshBasicMaterial color="#08121a" transparent opacity={0.85} />
      </mesh>
      <Billboard position={[0, 1.75, 0.35]}>
        <Text fontSize={0.34} color={color} anchorX="center" anchorY="middle" outlineWidth={0.01} outlineColor="#02060a" letterSpacing={0.1} maxWidth={3}>{label}</Text>
      </Billboard>
      <pointLight position={[0, 1.6, 0.8]} color={color} intensity={2} distance={5} />
    </group>
  );
}

export function Molecule({ color = COLORS.violet }) {
  const g = useRef();
  const nodes = useMemo(() => {
    const a = [];
    for (let i = 0; i < 14; i++) {
      a.push([(Math.random() - 0.5) * 3.4, (Math.random() - 0.3) * 3 + 2.6, (Math.random() - 0.5) * 3.4]);
    }
    return a;
  }, []);
  useFrame((_, d) => { if (g.current) g.current.rotation.y += d * 0.3; });
  return (
    <group ref={g} position={[0, 0, 0]}>
      {nodes.map((n, i) => (
        <group key={i}>
          <mesh position={n}><sphereGeometry args={[0.22, 18, 18]} /><meshStandardMaterial color={i % 3 ? color : COLORS.cyan} emissive={i % 3 ? color : COLORS.cyan} emissiveIntensity={0.5} metalness={0.3} roughness={0.2} /></mesh>
          {i > 0 && (() => {
            const a = new THREE.Vector3(...nodes[i - 1]); const b = new THREE.Vector3(...n);
            const mid = a.clone().add(b).multiplyScalar(0.5); const len = a.distanceTo(b);
            const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
            return <mesh position={mid} quaternion={q}><cylinderGeometry args={[0.03, 0.03, len, 6]} /><meshBasicMaterial color="#5de6ff" transparent opacity={0.5} /></mesh>;
          })()}
        </group>
      ))}
    </group>
  );
}
